import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Crown, Loader2, Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Chase Kings — leaderboard of agents by follow-up chase win rate.
 *
 * Small widget for the Team Pulse tab. Sources `agent_chase_stats`
 * (populated by the Won it! / Didn't land buttons on revivable deals).
 * Only agents with >= 2 attempts appear so a single lucky win can't
 * top the board.
 */
const ChaseKingsCard = ({ domainId }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = new URLSearchParams();
      if (domainId) p.set('domain_id', domainId);
      p.set('min_attempts', '2');
      p.set('limit', '5');
      const r = await axios.get(`${API}/analytics/chase-leaderboard?${p.toString()}`);
      setRows(r.data?.rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load chase leaderboard.');
    } finally { setLoading(false); }
  }, [domainId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4" data-testid="chase-kings-card">
      <div className="flex items-center gap-2 mb-3">
        <Crown className="w-4 h-4 text-amber-600" />
        <p className="text-sm font-bold text-slate-900">Chase Kings</p>
        <span className="text-[11px] text-slate-500">Top follow-up performers (last 30d)</span>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading leaderboard…
        </div>
      )}
      {error && (
        <p className="text-xs text-red-700 italic py-2">{error}</p>
      )}
      {!loading && !error && rows.length === 0 && (
        <p className="text-xs text-slate-500 italic py-2">
          No follow-up chases marked yet. Once agents start clicking "Won it!" / "Didn't land"
          on the Recently Contacted tab, the leaderboard will populate.
        </p>
      )}
      {rows.length > 0 && (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div
              key={r.agent_id}
              className="flex items-center gap-2 px-2 py-1.5 rounded bg-white border border-slate-100"
              data-testid={`chase-king-row-${r.agent_id}`}
            >
              <span
                className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold shrink-0 ${
                  i === 0 ? 'bg-amber-500'
                  : i === 1 ? 'bg-slate-400'
                  : i === 2 ? 'bg-amber-700'
                  : 'bg-slate-300'
                }`}
                title={`#${i + 1}`}
              >
                {i === 0 ? <Trophy className="w-3 h-3" /> : i + 1}
              </span>
              <span className="text-sm font-semibold text-slate-800 truncate flex-1">
                {r.agent_name?.split(' · ')[0] || '—'}
              </span>
              <span className="text-[11px] tabular-nums text-slate-600">
                {r.chase_wins}/{r.chase_attempts}
              </span>
              <span className="text-sm font-bold tabular-nums text-emerald-700 w-12 text-right">
                {Math.round(r.win_rate * 100)}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChaseKingsCard;
